/**
 * FM Reporting Dashboard
 * ======================
 * Operations, financial, compliance, and contractor performance reporting.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  BarChart3, TrendingUp, AlertTriangle, CheckCircle2, Clock, 
  FileText, DollarSign, Users, Building2, RefreshCw, ChevronRight,
  ArrowUp, ArrowDown, Minus
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL || '';

// Period options
const PERIODS = [
  { value: 'last_7', label: 'Last 7 Days' },
  { value: 'rolling_30', label: 'Last 30 Days' },
  { value: 'this_month', label: 'This Month' },
  { value: 'last_month', label: 'Last Month' },
  { value: 'quarter', label: 'Quarter' },
];

// Tier colors
const TIER_COLORS = {
  'A+': 'bg-emerald-100 text-emerald-800 border-emerald-200',
  'A': 'bg-green-100 text-green-800 border-green-200',
  'B': 'bg-blue-100 text-blue-800 border-blue-200',
  'C': 'bg-yellow-100 text-yellow-800 border-yellow-200',
  'D': 'bg-red-100 text-red-800 border-red-200',
};

export default function FMReportingPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [period, setPeriod] = useState('rolling_30');
  const [snapshot, setSnapshot] = useState(null);
  const [contractors, setContractors] = useState([]);
  const [sortBy, setSortBy] = useState('score_desc');

  // Fetch FM reporting data
  const fetchData = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      // Fetch FM summary and contractor performance in parallel
      const [summaryRes, contractorsRes] = await Promise.all([
        axios.get(`${API}/api/reporting/fm-summary?period=${period}`, { headers }),
        axios.get(`${API}/api/performance/contractors?period=${period}&sort_by=${sortBy}`, { headers })
      ]);

      if (summaryRes.data.ok) {
        setSnapshot(summaryRes.data.snapshot);
      }

      if (contractorsRes.data.ok) {
        setContractors(contractorsRes.data.contractors || []);
      }
    } catch (err) {
      console.error('Error fetching reporting data:', err);
      toast.error('Failed to load reporting data');
    } finally {
      setLoading(false);
    }
  }, [period, sortBy]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Refresh snapshots
  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/api/reporting/snapshot/rebuild?period=${period}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Snapshots refreshed');
      fetchData();
    } catch (err) {
      toast.error('Failed to refresh snapshots');
    } finally {
      setRefreshing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin h-8 w-8 border-4 border-teal-500 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  const kpis = snapshot?.kpis || {};
  const operations = snapshot?.operations || {};
  const financial = snapshot?.financial || {};
  const compliance = snapshot?.compliance || {};
  const efficiency = snapshot?.efficiency || {};

  return (
    <div className="min-h-screen bg-slate-50" data-testid="fm-reporting-page">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-slate-900">FM Reporting Dashboard</h1>
              <p className="text-slate-500">Operations, performance & compliance insights</p>
            </div>
            <div className="flex items-center gap-4">
              {/* Period Selector */}
              <select
                value={period}
                onChange={(e) => setPeriod(e.target.value)}
                className="px-4 py-2 rounded-xl border border-slate-200 bg-white text-slate-800 focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20"
                data-testid="period-selector"
              >
                {PERIODS.map(p => (
                  <option key={p.value} value={p.value}>{p.label}</option>
                ))}
              </select>
              
              {/* Refresh Button */}
              <Button
                onClick={handleRefresh}
                variant="outline"
                className="gap-2"
                disabled={refreshing}
                data-testid="refresh-btn"
              >
                <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8 space-y-8">
        
        {/* KPI Cards */}
        <section>
          <h2 className="text-lg font-semibold text-slate-800 mb-4">Key Metrics</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-4">
            <KPICard 
              label="Open Jobs" 
              value={kpis.open_jobs || 0} 
              icon={FileText}
              color="blue"
            />
            <KPICard 
              label="Overdue" 
              value={kpis.overdue_jobs || 0} 
              icon={AlertTriangle}
              color="red"
              alert={kpis.overdue_jobs > 0}
            />
            <KPICard 
              label="Completed" 
              value={operations.completed_jobs || 0} 
              icon={CheckCircle2}
              color="green"
            />
            <KPICard 
              label="Invoice Pending" 
              value={kpis.invoice_pending || 0} 
              icon={DollarSign}
              color="amber"
            />
            <KPICard 
              label="Invoice Paid" 
              value={kpis.invoice_paid || 0} 
              icon={DollarSign}
              color="emerald"
            />
            <KPICard 
              label="PPM Overdue" 
              value={kpis.ppm_overdue || 0} 
              icon={Clock}
              color="orange"
              alert={kpis.ppm_overdue > 0}
            />
            <KPICard 
              label="Compliance Open" 
              value={kpis.compliance_open || 0} 
              icon={AlertTriangle}
              color="purple"
            />
            <KPICard 
              label="Grouping Opps" 
              value={kpis.grouping_opportunities || 0} 
              icon={Building2}
              color="cyan"
            />
          </div>
        </section>

        {/* Main Grid */}
        <div className="grid lg:grid-cols-3 gap-8">
          
          {/* Contractor Performance Table */}
          <section className="lg:col-span-2">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <Users className="h-5 w-5 text-teal-600" />
                  Contractor Performance
                </CardTitle>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="text-sm px-3 py-1.5 rounded-lg border border-slate-200"
                >
                  <option value="score_desc">Score (High to Low)</option>
                  <option value="score_asc">Score (Low to High)</option>
                  <option value="jobs_completed">Jobs Completed</option>
                  <option value="name">Name</option>
                </select>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-slate-200">
                        <th className="text-left py-3 px-2 font-medium text-slate-600">Contractor</th>
                        <th className="text-center py-3 px-2 font-medium text-slate-600">Score</th>
                        <th className="text-center py-3 px-2 font-medium text-slate-600">Tier</th>
                        <th className="text-center py-3 px-2 font-medium text-slate-600">Jobs</th>
                        <th className="text-center py-3 px-2 font-medium text-slate-600">Response</th>
                        <th className="text-center py-3 px-2 font-medium text-slate-600">Docs %</th>
                        <th className="text-center py-3 px-2 font-medium text-slate-600">Invoice %</th>
                        <th className="text-right py-3 px-2 font-medium text-slate-600">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {contractors.length > 0 ? contractors.map((c, idx) => (
                        <tr 
                          key={c.contractor_id} 
                          className="border-b border-slate-100 hover:bg-slate-50 transition-colors"
                          data-testid={`contractor-row-${c.contractor_id}`}
                        >
                          <td className="py-3 px-2">
                            <div className="font-medium text-slate-800">{c.contractor_name}</div>
                            {c.risk_flags?.length > 0 && (
                              <div className="flex gap-1 mt-1">
                                {c.risk_flags.slice(0, 2).map((flag, i) => (
                                  <span key={i} className="text-xs bg-red-100 text-red-700 px-1.5 py-0.5 rounded">
                                    {flag.split(' ')[0]}...
                                  </span>
                                ))}
                              </div>
                            )}
                          </td>
                          <td className="py-3 px-2 text-center">
                            <span className={`font-bold ${
                              c.total_score >= 80 ? 'text-emerald-600' :
                              c.total_score >= 60 ? 'text-amber-600' :
                              'text-red-600'
                            }`}>
                              {c.total_score?.toFixed(0) || 'N/A'}
                            </span>
                          </td>
                          <td className="py-3 px-2 text-center">
                            <Badge className={`${TIER_COLORS[c.tier] || 'bg-slate-100'} border`}>
                              {c.tier || 'N/A'}
                            </Badge>
                          </td>
                          <td className="py-3 px-2 text-center text-slate-700">
                            {c.jobs_completed || 0}
                          </td>
                          <td className="py-3 px-2 text-center">
                            {c.avg_response_hours ? (
                              <span className={c.avg_response_hours <= 6 ? 'text-green-600' : c.avg_response_hours <= 24 ? 'text-amber-600' : 'text-red-600'}>
                                {c.avg_response_hours.toFixed(1)}h
                              </span>
                            ) : '-'}
                          </td>
                          <td className="py-3 px-2 text-center">
                            {c.documentation_rate != null ? (
                              <span className={c.documentation_rate >= 90 ? 'text-green-600' : c.documentation_rate >= 70 ? 'text-amber-600' : 'text-red-600'}>
                                {c.documentation_rate.toFixed(0)}%
                              </span>
                            ) : '-'}
                          </td>
                          <td className="py-3 px-2 text-center">
                            {c.invoice_accuracy != null ? (
                              <span className={c.invoice_accuracy >= 90 ? 'text-green-600' : c.invoice_accuracy >= 70 ? 'text-amber-600' : 'text-red-600'}>
                                {c.invoice_accuracy.toFixed(0)}%
                              </span>
                            ) : '-'}
                          </td>
                          <td className="py-3 px-2 text-right">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => navigate(`/contractors/${c.contractor_id}/performance`)}
                              className="gap-1"
                            >
                              View <ChevronRight className="h-4 w-4" />
                            </Button>
                          </td>
                        </tr>
                      )) : (
                        <tr>
                          <td colSpan={8} className="py-8 text-center text-slate-500">
                            No contractor data for this period
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </section>

          {/* Right Sidebar - Summaries */}
          <section className="space-y-6">
            
            {/* Financial Summary */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <DollarSign className="h-5 w-5 text-emerald-600" />
                  Financial Summary
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <SummaryRow 
                  label="Approved Value" 
                  value={`£${(financial.totals?.approved_value || 0).toLocaleString()}`}
                />
                <SummaryRow 
                  label="Invoiced Value" 
                  value={`£${(financial.totals?.invoiced_value || 0).toLocaleString()}`}
                />
                <SummaryRow 
                  label="Paid Value" 
                  value={`£${(financial.totals?.paid_value || 0).toLocaleString()}`}
                  highlight="green"
                />
                <SummaryRow 
                  label="Outstanding" 
                  value={`£${(financial.totals?.outstanding_value || 0).toLocaleString()}`}
                  highlight={financial.totals?.outstanding_value > 0 ? "amber" : ""}
                />
                <div className="pt-2 border-t border-slate-100">
                  <SummaryRow 
                    label="Avg Invoice Variance" 
                    value={`${(financial.invoices?.avg_variance_percent || 0).toFixed(1)}%`}
                  />
                </div>
              </CardContent>
            </Card>

            {/* Compliance Summary */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <CheckCircle2 className="h-5 w-5 text-blue-600" />
                  Compliance Summary
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <SummaryRow 
                  label="PPM Due" 
                  value={compliance.ppm?.due || 0}
                />
                <SummaryRow 
                  label="PPM Overdue" 
                  value={compliance.ppm?.overdue || 0}
                  highlight={compliance.ppm?.overdue > 0 ? "red" : ""}
                />
                <SummaryRow 
                  label="Cert Compliance" 
                  value={`${(compliance.certificates?.compliance_rate || 100).toFixed(0)}%`}
                  highlight={compliance.certificates?.compliance_rate < 90 ? "amber" : "green"}
                />
                <SummaryRow 
                  label="Doc Compliance" 
                  value={`${(compliance.documentation?.compliance_rate || 100).toFixed(0)}%`}
                  highlight={compliance.documentation?.compliance_rate < 90 ? "amber" : "green"}
                />
                <SummaryRow 
                  label="Open Cases" 
                  value={compliance.compliance_cases?.open || 0}
                />
              </CardContent>
            </Card>

            {/* Efficiency Summary */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <TrendingUp className="h-5 w-5 text-purple-600" />
                  Routing Efficiency
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <SummaryRow 
                  label="Total Visits" 
                  value={efficiency.total_visits || 0}
                />
                <SummaryRow 
                  label="Grouped Visits" 
                  value={efficiency.grouped_visits || 0}
                />
                <SummaryRow 
                  label="Grouping Rate" 
                  value={`${(efficiency.grouping_rate || 0).toFixed(0)}%`}
                />
                <SummaryRow 
                  label="Avg Jobs/Visit" 
                  value={(efficiency.avg_jobs_per_grouped_visit || 0).toFixed(1)}
                />
              </CardContent>
            </Card>

          </section>
        </div>

        {/* Site Summary Table */}
        <section>
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building2 className="h-5 w-5 text-indigo-600" />
                Site Summary (Top 20)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-200">
                      <th className="text-left py-3 px-2 font-medium text-slate-600">Site</th>
                      <th className="text-center py-3 px-2 font-medium text-slate-600">Open</th>
                      <th className="text-center py-3 px-2 font-medium text-slate-600">Overdue</th>
                      <th className="text-center py-3 px-2 font-medium text-slate-600">PPM Overdue</th>
                      <th className="text-center py-3 px-2 font-medium text-slate-600">Completed</th>
                      <th className="text-center py-3 px-2 font-medium text-slate-600">Contractors</th>
                      <th className="text-center py-3 px-2 font-medium text-slate-600">Grouping Opps</th>
                    </tr>
                  </thead>
                  <tbody>
                    {snapshot?.sites?.sites?.slice(0, 20).map((site, idx) => (
                      <tr 
                        key={site.site_id || idx}
                        className="border-b border-slate-100 hover:bg-slate-50"
                      >
                        <td className="py-3 px-2 font-medium text-slate-800">{site.site_name}</td>
                        <td className="py-3 px-2 text-center">{site.open_jobs}</td>
                        <td className="py-3 px-2 text-center">
                          <span className={site.overdue_jobs > 0 ? 'text-red-600 font-medium' : ''}>
                            {site.overdue_jobs}
                          </span>
                        </td>
                        <td className="py-3 px-2 text-center">
                          <span className={site.ppm_overdue > 0 ? 'text-orange-600 font-medium' : ''}>
                            {site.ppm_overdue}
                          </span>
                        </td>
                        <td className="py-3 px-2 text-center text-green-600">{site.completed_jobs}</td>
                        <td className="py-3 px-2 text-center">{site.contractors_used}</td>
                        <td className="py-3 px-2 text-center">
                          {site.grouping_opportunities > 0 && (
                            <span className="text-cyan-600 font-medium">{site.grouping_opportunities}</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </section>

      </div>
    </div>
  );
}

// KPI Card Component
function KPICard({ label, value, icon: Icon, color, alert }) {
  const colors = {
    blue: 'bg-blue-50 text-blue-700 border-blue-200',
    red: 'bg-red-50 text-red-700 border-red-200',
    green: 'bg-green-50 text-green-700 border-green-200',
    amber: 'bg-amber-50 text-amber-700 border-amber-200',
    emerald: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    orange: 'bg-orange-50 text-orange-700 border-orange-200',
    purple: 'bg-purple-50 text-purple-700 border-purple-200',
    cyan: 'bg-cyan-50 text-cyan-700 border-cyan-200',
  };

  return (
    <div className={`rounded-2xl border p-4 ${colors[color] || colors.blue} ${alert ? 'ring-2 ring-red-400' : ''}`}>
      <div className="flex items-center gap-2 mb-2">
        <Icon className="h-4 w-4 opacity-70" />
        <span className="text-xs font-medium opacity-80">{label}</span>
      </div>
      <div className="text-2xl font-bold">{value}</div>
    </div>
  );
}

// Summary Row Component
function SummaryRow({ label, value, highlight }) {
  const highlightClasses = {
    green: 'text-emerald-600',
    red: 'text-red-600',
    amber: 'text-amber-600',
  };

  return (
    <div className="flex justify-between items-center">
      <span className="text-sm text-slate-600">{label}</span>
      <span className={`font-medium ${highlightClasses[highlight] || 'text-slate-800'}`}>
        {value}
      </span>
    </div>
  );
}
